import { TreeManagementPersistor } from '../persistence/Persistor';
import { UserTree } from '@skill-trees/shared';

export interface TreeManagementViewState {
    trees: UserTree[];
    isLoading: boolean;
    error: string | null;
}

export class TreeManagementInteractor {
    private trees: UserTree[] = [];
    // Starts true: the page always fetches on mount, and starting false made the
    // pre-fetch render claim the user has no trees.
    private isLoading: boolean = true;
    private error: string | null = null;
    private fetchGeneration: number = 0;
    private updateCallback?: (state: TreeManagementViewState) => void;

    constructor(private readonly persistor: TreeManagementPersistor) {}

    getViewState(): TreeManagementViewState {
        return {
            trees: this.trees,
            isLoading: this.isLoading,
            error: this.error
        };
    }

    /** The view subscribes here; every internal state change pushes a fresh view state. */
    setUpdateCallback(callback: (state: TreeManagementViewState) => void): void {
        this.updateCallback = callback;
    }

    private notify(): void {
        this.updateCallback?.(this.getViewState());
    }

    async fetchUserTrees(): Promise<void> {
        const currentFetchGeneration = ++this.fetchGeneration;
        this.isLoading = true;
        this.error = null;
        this.notify();

        try {
            const fetchedTrees = await this.persistor.fetchUserTrees();
            if (currentFetchGeneration !== this.fetchGeneration) {
                return;
            }
            this.trees = fetchedTrees;
            this.isLoading = false;
        } catch (error) {
            if (currentFetchGeneration !== this.fetchGeneration) {
                return;
            }
            this.isLoading = false;
            this.error = error instanceof Error ? error.message : 'Failed to fetch trees';
            this.trees = [];
        }
        this.notify();
    }

    async renameTree(treeId: string, newName: string): Promise<void> {
        this.error = null;

        const treeIndex = this.trees.findIndex(t => t.slug === treeId);
        const originalName = treeIndex >= 0 ? this.trees[treeIndex].name : null;

        if (treeIndex >= 0) {
            this.trees[treeIndex] = { ...this.trees[treeIndex], name: newName };
            this.notify(); // optimistic rename
        }

        try {
            const result = await this.persistor.renameTree(treeId, newName);

            if (!result.success) {
                if (treeIndex >= 0 && originalName !== null) {
                    this.trees[treeIndex] = { ...this.trees[treeIndex], name: originalName };
                }
                this.error = result.error;
                this.notify();
                return;
            }

            await this.fetchUserTrees();
        } catch (error) {
            if (treeIndex >= 0 && originalName !== null) {
                this.trees[treeIndex] = { ...this.trees[treeIndex], name: originalName };
            }
            this.error = error instanceof Error ? error.message : 'Failed to rename tree';
            this.notify();
        }
    }

    async deleteTree(treeId: string): Promise<void> {
        this.error = null;

        const originalTrees = [...this.trees];
        this.trees = this.trees.filter(t => t.slug !== treeId);
        this.notify(); // optimistic removal

        try {
            const result = await this.persistor.deleteTree(treeId);

            if (!result.success) {
                this.trees = originalTrees;
                this.error = result.error;
                this.notify();
                return;
            }

            await this.fetchUserTrees();
        } catch (error) {
            this.trees = originalTrees;
            this.error = error instanceof Error ? error.message : 'Failed to delete tree';
            this.notify();
        }
    }

    async toggleTreeVisibility(treeId: string): Promise<void> {
        this.error = null;

        const treeIndex = this.trees.findIndex(t => t.slug === treeId);
        const originalIsPrivate = treeIndex >= 0 ? this.trees[treeIndex].isPrivate : null;

        if (treeIndex >= 0) {
            this.trees[treeIndex] = {
                ...this.trees[treeIndex],
                isPrivate: !this.trees[treeIndex].isPrivate
            };
            this.notify(); // optimistic toggle
        }

        try {
            const result = await this.persistor.toggleTreeVisibility(treeId);

            if (!result.success) {
                this.rollbackIsPrivate(treeIndex, originalIsPrivate);
                this.error = result.error;
                this.notify();
                return;
            }

            await this.fetchUserTrees();
        } catch (error) {
            this.rollbackIsPrivate(treeIndex, originalIsPrivate);
            this.error = error instanceof Error ? error.message : 'Failed to toggle tree visibility';
            this.notify();
        }
    }

    private rollbackIsPrivate(treeIndex: number, originalIsPrivate: boolean | null): void {
        if (treeIndex >= 0 && originalIsPrivate !== null) {
            this.trees[treeIndex] = { 
                ...this.trees[treeIndex], 
                isPrivate: originalIsPrivate 
            };
        }
    }
}

